/**
 * Paridade com o handler.py da Lambda mgs-lambda-etq (descontinuada) — mantido como
 * referência histórica do algoritmo; esta implementação é a canônica.
 * Mede largura tipográfica (pt) com fontkit + Montserrat TTF.
 * Sem fs/path — compatível com Webpack 5 / CRA (browser).
 *
 * Layout com subgrades: limites fixos legados (nome/raridade/edição/infos).
 * Layout simples (vazio/authentic/no_subgrades): na mesma linha, esquerda e
 * direita não podem se sobrepor; área útil = posições do SIMPLE_LAYOUT.
 */
import * as fontkit from "fontkit";
import { SIMPLE_LAYOUT, SIMPLE_LAYOUT_WIDTHS } from "./layout/constants";
import { LAYOUT_WITH_SUBGRADES, pickLayout } from "./pickLayout";
import { detectCardScenario } from "./partitionByPaper";
import { autographLineText, gradeLineText } from "./gradeHelpers";

const FIELD_SPECS = [
  {
    key: "nome",
    limit: 100.9,
    fontKey: "bold",
    size: 8.5,
    getText: (card) => card?.nome ?? "",
  },
  {
    key: "raridade",
    limit: 43.48,
    fontKey: "light",
    size: 4.5,
    getText: (card) => card?.raridade ?? "",
  },
  {
    key: "edicao",
    limit: 114.29,
    fontKey: "light",
    size: 4.5,
    getText: (card) => card?.edition ?? "",
  },
  {
    key: "infos",
    limit: 69.43,
    fontKey: "light",
    size: 4.5,
    getText: (card) => `| ${card?.ano || ""} | ${card?.idioma || ""} | ${card?.num || ""}`,
  },
];

/** Fonte fontkit da família declarada no layout: medir com a mesma face que desenha. */
const fontForFamily = (fonts, family) => (/Light/i.test(family) ? fonts.light : fonts.bold);

/** Layout simples: todos os campos desenham em SIMPLE_LAYOUT.*.font[0] (Montserrat-Bold). */
const simpleFont = (fonts) => fontForFamily(fonts, SIMPLE_LAYOUT.name.font[0]);

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const resolveScenario = (card) => {
  if (card?.scenario) return card.scenario;
  try {
    return detectCardScenario(card);
  } catch {
    return "with_subgrades";
  }
};

let cachedFonts = null;
let fontsPromise = null;

const stringWidth = (font, text, size) => {
  if (text === null || text === undefined || String(text) === "") return 0;
  const run = font.layout(String(text));
  return (run.advanceWidth / font.unitsPerEm) * size;
};

const fontsFromBuffers = (boldBuf, lightBuf) => {
  const toUint8 = (buf) =>
    buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return {
    bold: fontkit.create(toUint8(boldBuf)),
    light: fontkit.create(toUint8(lightBuf)),
  };
};

/** Injeta buffers (Jest / preload). */
const setFontsFromBuffers = (boldBuf, lightBuf) => {
  cachedFonts = fontsFromBuffers(boldBuf, lightBuf);
  return cachedFonts;
};

const getFonts = () => cachedFonts;

/**
 * Browser: passa URLs webpack dos TTFs (fetch).
 * Sem URLs e sem cache: retorna null (não usa fs — quebra o CRA).
 */
const ensureFontsLoaded = async (boldUrl, lightUrl) => {
  if (cachedFonts) return cachedFonts;
  if (!boldUrl || !lightUrl || typeof fetch === "undefined") {
    return cachedFonts;
  }
  if (!fontsPromise) {
    fontsPromise = Promise.all([
      fetch(boldUrl).then((r) => r.arrayBuffer()),
      fetch(lightUrl).then((r) => r.arrayBuffer()),
    ])
      .then(([boldAb, lightAb]) => setFontsFromBuffers(boldAb, lightAb))
      .catch((err) => {
        fontsPromise = null;
        throw err;
      });
  }
  return fontsPromise;
};

/**
 * Compat testes: só retorna cache. Carregue antes com setFontsFromBuffers.
 */
const loadFontsSync = () => {
  if (cachedFonts) return cachedFonts;
  throw new Error(
    "Fontes não carregadas. No browser: preloadTagFonts(); nos testes: setFontsFromBuffers(...)."
  );
};

const resolveFonts = (fonts) => fonts || cachedFonts;

const measureField = (card, spec, fonts = resolveFonts()) => {
  if (!fonts) {
    const text = spec.getText(card);
    return { key: spec.key, text, width: 0, limit: spec.limit, ok: true };
  }
  const text = spec.getText(card);
  const width = stringWidth(fonts[spec.fontKey], text, spec.size);
  return {
    key: spec.key,
    text,
    width,
    limit: spec.limit,
    ok: width <= spec.limit,
  };
};

const pushError = (fields, key, value, measured, limit) => {
  fields.push({
    key,
    value: String(value ?? ""),
    excedido: String(measured),
    limite: String(limit),
  });
};

const validateSingleField = (fields, fonts, key, text, size, maxWidth) => {
  if (isBlank(text)) return;
  const width = stringWidth(simpleFont(fonts), text, size);
  if (width > maxWidth) pushError(fields, key, text, width, maxWidth);
};

/**
 * Mesma linha: texto à esquerda (origem leftX) vs à direita (âncora rightAnchorX).
 * Espaço compartilhado — se a direita é curta, sobra largura para a esquerda e vice-versa.
 */
const validateLineCollision = (
  fields,
  fonts,
  {
    leftText,
    leftSize,
    leftX,
    rightText,
    rightSize,
    rightAnchorX,
    keyLeft,
    keyRight,
    minGap = 0,
  }
) => {
  const font = simpleFont(fonts);
  const leftWidth = isBlank(leftText) ? 0 : stringWidth(font, leftText, leftSize);
  const rightWidth = isBlank(rightText) ? 0 : stringWidth(font, rightText, rightSize);

  if (rightWidth > 0) {
    const rightStart = rightAnchorX - rightWidth;
    const maxLeftWidth = Math.max(0, rightStart - leftX - minGap);
    if (leftWidth > maxLeftWidth) {
      pushError(fields, keyLeft, leftText, leftWidth, maxLeftWidth);
    }
    const maxRightWidth = Math.max(0, rightAnchorX - leftX - leftWidth - minGap);
    if (rightWidth > maxRightWidth) {
      pushError(fields, keyRight, rightText, rightWidth, maxRightWidth);
    }
  } else if (leftWidth > rightAnchorX - leftX) {
    pushError(fields, keyLeft, leftText, leftWidth, rightAnchorX - leftX);
  }
};

const validateSubgradesCard = (card, fonts) => {
  const fields = [];
  for (const spec of FIELD_SPECS) {
    const m = measureField(card, spec, fonts);
    if (!m.ok) {
      pushError(fields, m.key, m.text, m.width, m.limit);
    }
  }
  return fields;
};

const validateSimpleCard = (card, fonts, scenario) => {
  const fields = [];
  const isAuthentic = scenario === "authentic" || scenario === "authentic_auto";
  const isNoSub = scenario === "no_subgrades" || scenario === "no_subgrades_auto";
  const isAuto = scenario === "authentic_auto" || scenario === "no_subgrades_auto";

  validateLineCollision(fields, fonts, {
    leftText: card.nome,
    leftSize: SIMPLE_LAYOUT.name.font[1],
    leftX: SIMPLE_LAYOUT.name.x,
    rightText: card.num,
    rightSize: SIMPLE_LAYOUT.number.font[1],
    rightAnchorX: SIMPLE_LAYOUT.number.x,
    keyLeft: "nome",
    keyRight: "numeracao",
    minGap: 0,
  });

  if (isAuthentic) {
    validateLineCollision(fields, fonts, {
      leftText: card.edition,
      leftSize: SIMPLE_LAYOUT.edition.font[1],
      leftX: SIMPLE_LAYOUT.edition.x,
      rightText: "AUTHENTIC",
      rightSize: SIMPLE_LAYOUT.right_top.font[1],
      rightAnchorX: SIMPLE_LAYOUT.right_top.x,
      keyLeft: "edicao",
      keyRight: "nota_geral",
      minGap: 0,
    });
  } else {
    validateSingleField(
      fields,
      fonts,
      "edicao",
      card.edition,
      SIMPLE_LAYOUT.edition.font[1],
      SIMPLE_LAYOUT_WIDTHS.edition
    );
  }

  if (isNoSub) {
    validateLineCollision(fields, fonts, {
      leftText: card.raridade,
      leftSize: SIMPLE_LAYOUT.raridade.font[1],
      leftX: SIMPLE_LAYOUT.raridade.x,
      rightText: gradeLineText(card),
      rightSize: SIMPLE_LAYOUT.right_middle.font[1],
      rightAnchorX: SIMPLE_LAYOUT.right_middle.x,
      keyLeft: "raridade",
      keyRight: "nota_geral",
      minGap: 0,
    });
  } else {
    validateSingleField(
      fields,
      fonts,
      "raridade",
      card.raridade,
      SIMPLE_LAYOUT.raridade.font[1],
      SIMPLE_LAYOUT_WIDTHS.raridade
    );
  }

  if (isAuto) {
    validateLineCollision(fields, fonts, {
      leftText: card.ano,
      leftSize: SIMPLE_LAYOUT.ano.font[1],
      leftX: SIMPLE_LAYOUT.ano.x,
      rightText: autographLineText(card.notaAutografo),
      rightSize: SIMPLE_LAYOUT.right_auto.font[1],
      rightAnchorX: SIMPLE_LAYOUT.right_auto.x,
      keyLeft: "ano",
      keyRight: "nota_autografo",
      minGap: 0,
    });
  } else {
    validateSingleField(
      fields,
      fonts,
      "ano",
      card.ano,
      SIMPLE_LAYOUT.ano.font[1],
      SIMPLE_LAYOUT_WIDTHS.ano
    );
  }

  validateLineCollision(fields, fonts, {
    leftText: card.idioma,
    leftSize: SIMPLE_LAYOUT.idioma.font[1],
    leftX: SIMPLE_LAYOUT.idioma.x,
    rightText: card.serial,
    rightSize: SIMPLE_LAYOUT.serial.font[1],
    rightAnchorX: SIMPLE_LAYOUT.serial.x,
    keyLeft: "idioma",
    keyRight: "serial",
    minGap: 0,
  });

  return fields;
};

/**
 * @param {object[]} cards
 * @param {{ fonts?: object }} [opts]
 * @returns {{ cards: Array<{ serial: string, fields: object[] }> }}
 */
const limitString = (cards, opts = {}) => {
  const fonts = resolveFonts(opts.fonts);
  const out = { cards: [] };
  if (!fonts) return out;

  for (const card of cards || []) {
    const scenario = resolveScenario(card);
    const layout = pickLayout(scenario);
    const fields =
      layout === LAYOUT_WITH_SUBGRADES
        ? validateSubgradesCard(card, fonts)
        : validateSimpleCard(card, fonts, scenario);
    if (fields.length) {
      out.cards.push({ serial: card.serial, fields });
    }
  }
  return out;
};

/**
 * Maior prefixo de `text` cuja largura tipográfica (pt) ≤ `limit`.
 * Critério = stringWidth(fonte, tamanho) — igual ReportLab na Lambda.
 * A busca binária usa índice de caractere só como algoritmo; a decisão
 * de corte é sempre pela largura medida, não por contagem fixa de chars.
 */
const fitMaxPrefix = (text, font, size, limit) => {
  const raw = String(text ?? "");
  if (!raw) return "";
  if (stringWidth(font, raw, size) <= limit) return raw;
  let lo = 0;
  let hi = raw.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (stringWidth(font, raw.slice(0, mid), size) <= limit) lo = mid;
    else hi = mid - 1;
  }
  return raw.slice(0, lo);
};

const getSpec = (key) => FIELD_SPECS.find((s) => s.key === key);

/**
 * Limita texto de um campo simples (nome / raridade / edicao) à largura máxima
 * do layout com subgrades.
 * Sem fontes carregadas: devolve o valor sem clamp (preload ainda em andamento).
 */
const clampSimpleField = (fieldKey, nextValue, fonts = resolveFonts()) => {
  const raw = String(nextValue ?? "");
  const spec = getSpec(fieldKey);
  if (!spec || fieldKey === "infos" || !fonts) return raw;
  return fitMaxPrefix(nextValue, fonts[spec.fontKey], spec.size, spec.limit);
};

const collisionMaxLeft = (fonts, leftSize, leftX, rightText, rightSize, rightAnchorX) => {
  const font = simpleFont(fonts);
  const rightWidth = isBlank(rightText) ? 0 : stringWidth(font, rightText, rightSize);
  if (rightWidth > 0) {
    return Math.max(0, rightAnchorX - rightWidth - leftX);
  }
  return Math.max(0, rightAnchorX - leftX);
};

const collisionMaxRight = (fonts, leftText, leftSize, leftX, rightSize, rightAnchorX) => {
  const font = simpleFont(fonts);
  const leftWidth = isBlank(leftText) ? 0 : stringWidth(font, leftText, leftSize);
  return Math.max(0, rightAnchorX - leftX - leftWidth);
};

/**
 * Clamp no layout simples: colisão na linha ou área útil (SIMPLE_LAYOUT_WIDTHS).
 * @param {"nome"|"numeracao"|"num"|"edicao"|"raridade"|"ano"|"idioma"} fieldKey
 */
const clampSimpleLayoutField = (fieldKey, nextValue, card, fonts = resolveFonts()) => {
  const raw = String(nextValue ?? "");
  if (!fonts) return raw;
  const font = simpleFont(fonts);
  const scenario = resolveScenario(card);
  const isAuthentic = scenario === "authentic" || scenario === "authentic_auto";
  const isNoSub = scenario === "no_subgrades" || scenario === "no_subgrades_auto";
  const isAuto = scenario === "authentic_auto" || scenario === "no_subgrades_auto";

  if (fieldKey === "nome") {
    const max = collisionMaxLeft(
      fonts,
      SIMPLE_LAYOUT.name.font[1],
      SIMPLE_LAYOUT.name.x,
      card?.num,
      SIMPLE_LAYOUT.number.font[1],
      SIMPLE_LAYOUT.number.x
    );
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.name.font[1], max);
  }

  if (fieldKey === "numeracao" || fieldKey === "num") {
    const max = collisionMaxRight(
      fonts,
      card?.nome,
      SIMPLE_LAYOUT.name.font[1],
      SIMPLE_LAYOUT.name.x,
      SIMPLE_LAYOUT.number.font[1],
      SIMPLE_LAYOUT.number.x
    );
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.number.font[1], max);
  }

  if (fieldKey === "edicao") {
    const max = isAuthentic
      ? collisionMaxLeft(
          fonts,
          SIMPLE_LAYOUT.edition.font[1],
          SIMPLE_LAYOUT.edition.x,
          "AUTHENTIC",
          SIMPLE_LAYOUT.right_top.font[1],
          SIMPLE_LAYOUT.right_top.x
        )
      : SIMPLE_LAYOUT_WIDTHS.edition;
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.edition.font[1], max);
  }

  if (fieldKey === "raridade") {
    const max = isNoSub
      ? collisionMaxLeft(
          fonts,
          SIMPLE_LAYOUT.raridade.font[1],
          SIMPLE_LAYOUT.raridade.x,
          gradeLineText(card),
          SIMPLE_LAYOUT.right_middle.font[1],
          SIMPLE_LAYOUT.right_middle.x
        )
      : SIMPLE_LAYOUT_WIDTHS.raridade;
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.raridade.font[1], max);
  }

  if (fieldKey === "ano") {
    const max = isAuto
      ? collisionMaxLeft(
          fonts,
          SIMPLE_LAYOUT.ano.font[1],
          SIMPLE_LAYOUT.ano.x,
          autographLineText(card?.notaAutografo),
          SIMPLE_LAYOUT.right_auto.font[1],
          SIMPLE_LAYOUT.right_auto.x
        )
      : SIMPLE_LAYOUT_WIDTHS.ano;
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.ano.font[1], max);
  }

  if (fieldKey === "idioma") {
    const max = collisionMaxLeft(
      fonts,
      SIMPLE_LAYOUT.idioma.font[1],
      SIMPLE_LAYOUT.idioma.x,
      card?.serial,
      SIMPLE_LAYOUT.serial.font[1],
      SIMPLE_LAYOUT.serial.x
    );
    return fitMaxPrefix(raw, font, SIMPLE_LAYOUT.idioma.font[1], max);
  }

  return raw;
};

/**
 * Ao editar ano|idioma|num no layout com subgrades, reduz só a parte editada
 * até o composto infos caber.
 * @param {"ano"|"idioma"|"num"} part
 */
const clampInfosPart = (part, nextPartValue, card, fonts = resolveFonts()) => {
  const raw = String(nextPartValue ?? "");
  if (!fonts) return raw;
  const spec = getSpec("infos");
  const font = fonts[spec.fontKey];
  let ano = part === "ano" ? raw : String(card?.ano ?? "");
  let idioma = part === "idioma" ? raw : String(card?.idioma ?? "");
  let num = part === "num" ? raw : String(card?.num ?? "");

  const compose = () => `| ${ano} | ${idioma} | ${num}`;
  if (stringWidth(font, compose(), spec.size) <= spec.limit) {
    return part === "ano" ? ano : part === "idioma" ? idioma : num;
  }

  let edited = part === "ano" ? ano : part === "idioma" ? idioma : num;
  let lo = 0;
  let hi = edited.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const trial = edited.slice(0, mid);
    if (part === "ano") ano = trial;
    else if (part === "idioma") idioma = trial;
    else num = trial;
    if (stringWidth(font, compose(), spec.size) <= spec.limit) lo = mid;
    else hi = mid - 1;
  }
  return edited.slice(0, lo);
};

/**
 * Clamp consciente do cenário da carta.
 */
const clampFieldForCard = (fieldKey, nextValue, card, fonts = resolveFonts()) => {
  const layout = pickLayout(resolveScenario(card));
  if (layout === LAYOUT_WITH_SUBGRADES) {
    if (fieldKey === "ano" || fieldKey === "idioma" || fieldKey === "num") {
      return clampInfosPart(fieldKey, nextValue, card, fonts);
    }
    return clampSimpleField(fieldKey === "edicao" ? "edicao" : fieldKey, nextValue, fonts);
  }
  return clampSimpleLayoutField(fieldKey, nextValue, card, fonts);
};

const cardWithFieldValue = (card, fieldKey, value) => {
  if (fieldKey === "edicao") return { ...card, edition: value };
  if (fieldKey === "numeracao" || fieldKey === "num") return { ...card, num: value };
  if (fieldKey === "nome") return { ...card, nome: value };
  if (fieldKey === "raridade") return { ...card, raridade: value };
  if (fieldKey === "ano") return { ...card, ano: value };
  if (fieldKey === "idioma") return { ...card, idioma: value };
  return card;
};

/**
 * Clamp suave na digitação: se o valor já vinha estourado (pré-preenchido),
 * permite encurtar/editar sem “cortar tudo” de uma vez. Só impede crescer
 * enquanto ainda passa do limite; quando cabe, volta ao clamp duro.
 */
const softClampFieldForCard = (
  fieldKey,
  nextValue,
  card,
  previousValue,
  fonts = resolveFonts()
) => {
  const next = String(nextValue ?? "");
  const prev = String(previousValue ?? "");
  const fitted = clampFieldForCard(fieldKey, next, cardWithFieldValue(card, fieldKey, next), fonts);
  if (fitted === next) return next;
  // Ainda estoura: não fazer snap agressivo — só bloquear crescimento.
  if (next.length <= prev.length) return next;
  return prev;
};

/**
 * Erros de largura só para campos cujo valor atual ainda é o pré-preenchido
 * (usuário ainda não editou — aí mostramos vermelho). Campos já editados
 * são clampados na digitação e não entram no highlight.
 *
 * @param {object} errorData resultado de limitString
 * @param {object[]} cards tagsData atuais
 * @param {Record<string, object>} initialBySerial snapshot serial → campos
 */
const filterPrefilledOverflows = (errorData, cards, initialBySerial) => {
  const bySerial = Object.fromEntries((cards || []).map((c) => [c.serial, c]));
  const out = { cards: [] };
  for (const errCard of errorData?.cards || []) {
    const current = bySerial[errCard.serial];
    const initial = initialBySerial?.[errCard.serial];
    if (!current || !initial) continue;
    const fields = (errCard.fields || []).filter((f) => {
      if (f.key === "nome") return String(current.nome ?? "") === String(initial.nome ?? "");
      if (f.key === "raridade")
        return String(current.raridade ?? "") === String(initial.raridade ?? "");
      if (f.key === "edicao")
        return String(current.edition ?? "") === String(initial.edition ?? "");
      if (f.key === "ano") return String(current.ano ?? "") === String(initial.ano ?? "");
      if (f.key === "idioma")
        return String(current.idioma ?? "") === String(initial.idioma ?? "");
      if (f.key === "numeracao")
        return String(current.num ?? "") === String(initial.num ?? "");
      if (f.key === "infos") {
        return (
          String(current.ano ?? "") === String(initial.ano ?? "") &&
          String(current.idioma ?? "") === String(initial.idioma ?? "") &&
          String(current.num ?? "") === String(initial.num ?? "")
        );
      }
      return false;
    });
    if (fields.length) out.cards.push({ serial: errCard.serial, fields });
  }
  return out;
};

const snapshotTagFields = (card) => ({
  nome: card?.nome ?? "",
  raridade: card?.raridade ?? "",
  edition: card?.edition ?? "",
  ano: card?.ano ?? "",
  idioma: card?.idioma ?? "",
  num: card?.num ?? "",
});

/** Mapa serial → Set(fieldKey) para highlight rápido. */
const overflowKeysBySerial = (errorData) => {
  const map = {};
  for (const card of errorData?.cards || []) {
    map[card.serial] = new Set((card.fields || []).map((f) => f.key));
  }
  return map;
};

/** @internal testes */
const _resetFontsCache = () => {
  cachedFonts = null;
  fontsPromise = null;
};

export {
  FIELD_SPECS,
  stringWidth,
  setFontsFromBuffers,
  getFonts,
  loadFontsSync,
  ensureFontsLoaded,
  measureField,
  limitString,
  overflowKeysBySerial,
  fitMaxPrefix,
  clampSimpleField,
  clampInfosPart,
  clampSimpleLayoutField,
  clampFieldForCard,
  softClampFieldForCard,
  filterPrefilledOverflows,
  snapshotTagFields,
  _resetFontsCache,
};
